import { useEffect, useState } from 'react';
import { SUPPORTED_CLAIMS } from '@pryme/protocol';
import { Alert, api, ErrorAlert, Spinner, useAsyncAction } from '@pryme/ui';
import { useServiceOutlet } from '../components/ServiceLayout';
import { canAdmin, type ClaimConfig, type SecurityConfig } from '../lib/types';

export const CLAIM_INFO: Record<string, { label: string; detail: string; sdk: string }> = {
  'human.verified': { label: 'Verified person', detail: 'The person passed issuer-backed verification, for example MitID through Idura. Always included.', sdk: 'id.isHumanVerified()' },
  'age.over13': { label: 'Age 13 or over', detail: 'Yes/no, derived privately from the date of birth. The date itself is never shared.', sdk: 'id.isOver(13)' },
  'age.over15': { label: 'Age 15 or over', detail: 'Yes/no threshold. No date of birth is shared.', sdk: 'id.isOver(15)' },
  'age.over16': { label: 'Age 16 or over', detail: 'Yes/no threshold. No date of birth is shared.', sdk: 'id.isOver(16)' },
  'age.over18': { label: 'Age 18 or over', detail: 'Yes/no threshold. No date of birth is shared.', sdk: 'id.isOver(18)' },
  'age.over21': { label: 'Age 21 or over', detail: 'Yes/no threshold. No date of birth is shared.', sdk: 'id.isOver(21)' },
  'eid.scheme_country': { label: 'Country of the eID', detail: 'ISO country of the eID scheme the person verified with, for example DK for MitID. Not nationality, not residence: Pryme has no evidence of where a person lives.', sdk: 'id.eidSchemeCountry()' },
};

export function Claims() {
  const { service: s, reload } = useServiceOutlet();
  const [cfg, setCfg] = useState<ClaimConfig>(s.claimConfig);
  const [sec, setSec] = useState<SecurityConfig>(s.securityConfig);
  const save = useAsyncAction();
  const [saved, setSaved] = useState(false);
  const admin = canAdmin(s.role);
  useEffect(() => {
    setCfg(s.claimConfig);
    setSec(s.securityConfig);
  }, [s.claimConfig, s.securityConfig]);

  const toggle = (c: string) => {
    setSaved(false);
    setCfg((x) => ({ ...x, requested: x.requested.includes(c) ? x.requested.filter((y) => y !== c) : [...x.requested, c] }));
  };

  const submit = async () => {
    setSaved(false);
    const r = await save.run(() =>
      api(`/v1/services/${s.id}/config`, {
        method: 'PUT',
        body: {
          claims: { requested: cfg.requested, ttlSeconds: cfg.ttlSeconds, onUnavailable: cfg.onUnavailable, ...(cfg.minLevel ? { minLevel: cfg.minLevel } : {}) },
          security: { requireStepUpForLinking: !!sec.requireStepUpForLinking },
        },
      }),
    );
    if (r) {
      setSaved(true);
      void reload();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <section className="p-card" aria-labelledby="c-h">
        <h2 id="c-h">Requested claims</h2>
        <p className="p-small p-muted">
          Pryme discloses <strong>derived assertions only</strong>, such as <code>age.over18 = true</code>, never a birth date. People approve each claim on the consent screen and can withdraw it later.
        </p>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }} disabled={!admin}>
          <legend className="p-skip">Claims</legend>
          <ul className="dv-claims">
            {SUPPORTED_CLAIMS.map((c) => {
              const info = CLAIM_INFO[c];
              return (
                <li key={c}>
                  <label>
                    <input type="checkbox" checked={c === 'human.verified' || cfg.requested.includes(c)} disabled={c === 'human.verified'} onChange={() => toggle(c)} />
                    <span><strong>{info?.label}</strong> <code className="p-mono-chip">{c}</code></span>
                    <span className="dv-claim-sdk">{info?.sdk}</span>
                    <span className="dv-claim-detail">{info?.detail}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      </section>

      <section className="p-card" aria-labelledby="a-h">
        <h2 id="a-h">Assurance, freshness and unavailable claims</h2>
        <div className="p-grid">
          <div className="p-field">
            <label htmlFor="minLevel">Minimum assurance level</label>
            <select id="minLevel" className="p-select" disabled={!admin} value={cfg.minLevel ?? ''} onChange={(e) => { setSaved(false); setCfg((x) => ({ ...x, minLevel: (e.target.value || undefined) as ClaimConfig['minLevel'] })); }}>
              <option value="">No minimum (default)</option>
              <option value="low">low</option>
              <option value="substantial">substantial (eIDAS)</option>
              <option value="high">high (eIDAS, e.g. MitID high)</option>
            </select>
            <span className="p-hint">Evidence category is always disclosed too. Sandbox evidence (Idura test users, offline mock) is never real government verification regardless of level.</span>
          </div>
          <div className="p-field">
            <label htmlFor="ttl">Signed-state lifetime (seconds)</label>
            <input id="ttl" className="p-input" type="number" min={60} max={3600} step={30} disabled={!admin} value={cfg.ttlSeconds}
              onChange={(e) => { setSaved(false); setCfg((x) => ({ ...x, ttlSeconds: Number(e.target.value) })); }} />
            <span className="p-hint">60–3600 s. The SDK evaluates predicates locally while fresh, then refreshes online. Set stricter limits with <code>maxStateAgeSeconds</code>; the SDK never extends Pryme's expiry.</span>
          </div>
          <div className="p-field">
            <label htmlFor="unavail">When a requested claim is unavailable</label>
            <select id="unavail" className="p-select" disabled={!admin} value={cfg.onUnavailable} onChange={(e) => { setSaved(false); setCfg((x) => ({ ...x, onUnavailable: e.target.value as ClaimConfig['onUnavailable'] })); }}>
              <option value="deny">Deny: the predicate fails closed (false)</option>
              <option value="prompt">Prompt: the person decides on the consent screen</option>
            </select>
            <span className="p-hint">Missing claims are never silently “false”: <code>id.claimStatus(name)</code> distinguishes false, not_disclosed, unavailable, stale and rejected.</span>
          </div>
        </div>
      </section>

      <section className="p-card" aria-labelledby="l-h">
        <h2 id="l-h">Account linking</h2>
        <label className="p-check">
          <input type="checkbox" disabled={!admin} checked={!!sec.requireStepUpForLinking} onChange={(e) => { setSaved(false); setSec((x) => ({ ...x, requireStepUpForLinking: e.target.checked })); }} />
          <span>
            <strong>Require passkey step-up for account linking</strong>
            <span className="p-small p-muted" style={{ display: 'block' }}>
              Ask the person to confirm with their passkey again before Pryme links their pairwise identity to an existing account at your service. Your application should also require them to be signed in to that account first, which prevents link-swap and account takeover.
            </span>
          </span>
        </label>
      </section>

      <ErrorAlert error={save.error} />
      {saved && <Alert tone="ok">Saved. New logins and refreshes use this configuration.</Alert>}
      {admin ? (
        <button className="p-btn" disabled={save.busy}>{save.busy ? <Spinner label="Saving…" /> : 'Save claim and consent settings'}</button>
      ) : (
        <p className="p-muted p-small">Read-only: admins and owners can change these settings.</p>
      )}
    </form>
  );
}
